import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 768);
  const [openSubmenu, setOpenSubmenu] = useState(null);

  useEffect(() => {
    const checkScreenSize = () => setIsSmallScreen(window.innerWidth <= 768);
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const toggleSubmenu = (menu) => {
    setOpenSubmenu((prev) => (prev === menu ? null : menu));
  };

  const closeSidebarOnMobile = () => {
    if (isSmallScreen) setIsSidebarOpen(false);
  };

  const styles = {
    sidebar: {
      /* Mobile drawer: 30vw so width is always tied to the viewport, not a parent */
      width: isSmallScreen ? "30vw" : "250px",
      maxWidth: isSmallScreen ? "30vw" : undefined,
      boxSizing: "border-box",
      minWidth: 0,
      height: "calc(100vh - 70px)",
      backgroundColor: "#111",
      color: "white",
      position: "fixed",
      top: "70px",
      left: "0",
      overflowY: "auto",
      padding: "10px 0",
      zIndex: 900,
      boxShadow: "2px 0 8px rgba(0,0,0,0.3)",
      display: isSidebarOpen ? "block" : "none",
    },
    header: {
      textAlign: "center",
      textTransform: "uppercase",
      backgroundColor: "#FFC107",
      color: "#000",
      padding: isSmallScreen ? "8px 8px" : "10px 14px",
      marginBottom: "0",
      cursor: "pointer",
      fontSize: isSmallScreen ? "11px" : "14px",
      fontWeight: "bold",
      letterSpacing: isSmallScreen ? "0.3px" : "0.5px",
      userSelect: "none",
      overflowWrap: "break-word",
    },
    submenu: {
      listStyle: "none",
      padding: "4px 0 8px 0",
      margin: "0 0 4px 0",
      backgroundColor: "#1a1a1a",
      borderLeft: "3px solid #FFC107",
    },
    link: {
      color: "#ffffff",
      textDecoration: "none",
      display: "block",
      padding: isSmallScreen ? "6px 10px" : "7px 16px",
      fontSize: isSmallScreen ? "12px" : "13px",
      fontWeight: "normal",
      transition: "background 0.15s",
      overflowWrap: "break-word",
    },
  };

  const renderSubmenu = (menu, links) => (
    <ul style={{ ...styles.submenu, display: openSubmenu === menu ? "block" : "none" }}>
      {links.map(({ to, label }) => (
        <NavLink
          key={to}
          to={to}
          onClick={closeSidebarOnMobile}
          style={({ isActive }) => ({
            ...styles.link,
            backgroundColor: isActive ? "#FFC107" : "transparent",
            color: isActive ? "#000" : "#ffffff",
            fontWeight: isActive ? "bold" : "normal",
          })}
        >
          {label}
        </NavLink>
      ))}
    </ul>
  );

  return (
    <div style={styles.sidebar}>

      {/* Dashboard — Tracker Admin items merged here */}
      <h3 style={styles.header} onClick={() => toggleSubmenu("dashboard")}>
        Dashboard
      </h3>
      {renderSubmenu("dashboard", [
        { to: "/home",               label: "Home" },
        // { to: "/my-profile",         label: "My Profile" },
        { to: "/notification",       label: "Notifications" },
        // { to: "/my-requests",        label: "My Requests" },
        // { to: "/tracker-admin",      label: "Data Management" },
        { to: "/tracker-dashboard",  label: "Admin Dashboard" },
        { to: "/monthly-data-entry", label: "Monthly Data Entry" },
      ])}

      {/* Vehicle Management */}
      <h3 style={styles.header} onClick={() => toggleSubmenu("vehicle")}>
        Vehicle Management
      </h3>
      {renderSubmenu("vehicle", [
        { to: "/vehicle-management",  label: "Overview" },
        { to: "/vehicle-onboarding",  label: "Onboarding" },
        { to: "/vehicle-documents",   label: "Documents" },
        // { to: "/live-fleet-tracking", label: "Live Fleet Tracking" },
        { to: "/truck-maintenance",   label: "Truck Maintenance" },
        { to: "/diesel",              label: "Diesel" },
        { to: "/live-fleet-tracking", label: "Live Fleet Tracking" },
        { to: "/expenses", label: "Expenses" },
      ])}

      {/* Driver Management */}
      <h3 style={styles.header} onClick={() => toggleSubmenu("driver-management")}>
        Driver Management
      </h3>
      {renderSubmenu("driver-management", [
        { to: "/driver-management",          label: "Overview" },
        { to: "/driver-onboarding",          label: "Onboarding" },
        { to: "/driver-attendance-approval", label: "Attendance" },
        { to: "/attendance-records",         label: "Attendance Records" },
        { to: "/driver-leave-admin",         label: "Leave Requests" },
        { to: "/driver-advance",             label: "Advance" },
        { to: "/driver-deduction",           label: "Deduction" },
        { to: "/driver-salary",              label: "Salary" },
        { to: "/driver-timeSheet",           label: "Time Sheet" },
        { to: "/driver-onboarding-shift",    label: "Driver Shift" },
        { to: "/driver-liveTracking",        label: "Live Tracking" },
        { to: "/driver-vehicleTracking",     label: "Vehicle Tracking" },
        { to: "/vehicle-GPSIntegration",     label: "GPS Integration" },
      ])}

      {/* Vendor Management */}
      <h3 style={styles.header} onClick={() => toggleSubmenu("vendor-management")}>
        Vendor Management
      </h3>
      {renderSubmenu("vendor-management", [
        { to: "/vendor-management", label: "Overview" },
        { to: "/vendor-onboarding", label: "Onboarding" },
        { to: "/trip-sheet",        label: "Trip Sheet" },
        { to: "/advance",           label: "Advance" },
        { to: "/deduction",         label: "Deductions" },
        { to: "/payment",           label: "Payment" },
      ])}

      {/* Customer Management */}
      <h3 style={styles.header} onClick={() => toggleSubmenu("customer-management")}>
        Customer Management
      </h3>
      {renderSubmenu("customer-management", [
        { to: "/customer-management", label: "Overview" },
        { to: "/customer-onboarding", label: "Onboarding" },
        { to: "/agreement",           label: "Agreement" },
        { to: "/invoice",             label: "Invoice" },
        { to: "/gst-file",            label: "GST Filing" },
        { to: "/payment-status",      label: "Payment Status" },
        { to: "/mis",                 label: "MIS Reports" },
      ])}

      {/* Expense Management */}
      <h3 style={styles.header} onClick={() => toggleSubmenu("expense-management")}>
        Expense Management
      </h3>
      {renderSubmenu("expense-management", [
        { to: "/expenses-management", label: "Overview" },
        { to: "/vehicle-expenses",    label: "Vehicle Expenses" },
        { to: "/others",              label: "Other Expenses" },
      ])}

      {/* Employee Management */}
      <h3 style={styles.header} onClick={() => toggleSubmenu("employee-management")}>
        Employee Management
      </h3>
      {renderSubmenu("employee-management", [
        { to: "/employee-management",  label: "Overview" },
        { to: "/employee-list",        label: "Employee List" },
        { to: "/employee-onboarding",  label: "Employee onboarding" },
        { to: "/employee-passwords",   label: "Set Passwords" },
        { to: "/employee-attendance",  label: "Attendance Approvals" },
        { to: "/employee-att-records", label: "Attendance Records" },
        { to: "/employee-leave",       label: "Leave Requests" },
        { to: "/employee-advance",     label: "Advance Requests" },
        { to: "/employee-salary",      label: "Salary" },
        { to: "/employee-deduction",   label: "Deduction" },
        { to: "/employee-escalations", label: "Escalations" },
        { to: "/employee-schedule",    label: "Schedule Config" },
        { to: "/employee-timesheet",   label: "Timesheet" },
      ])}

      {/* Help & Support */}
      <h3 style={styles.header} onClick={() => toggleSubmenu("help-support")}>
        Help & Support
      </h3>
      {renderSubmenu("help-support", [
        { to: "/help-support", label: "Help & Support" },
        { to: "/my-requests",  label: "My Requests" },
      ])}

    </div>
  );
};

export default Sidebar;
